import { inject, Signal } from "@angular/core";
import { toSignal, ToSignalOptions } from "@angular/core/rxjs-interop";

import {
    FEATURE_FLAGS,
    FeatureFlagName,
} from "../constants/feature-flags.constant";
import { FeatureFlagFacade } from "../facades/feature-flag.facade";

export type InjectFeatureFlagOptions = Omit<
    ToSignalOptions<boolean>,
    "initialValue" | "requireSync"
> & {
    /**
     * Initial value when feature flags are not loaded yet
     */
    initialValue?: boolean;
};

function getDefaultValue(flagName: FeatureFlagName): boolean {
    return (
        FEATURE_FLAGS.find((flag) => flag.flagName === flagName)
            ?.defaultValue ?? false
    );
}

/**
 * Return a Signal that reflects a feature-flag value
 *
 * This is useful in a reactive context.
 *
 * Consider using {@link injectLoadedFeatureFlag} instead in a non-reactive context when
 * the actual (loaded) value must be read.
 *
 * @see injectLoadedFeatureFlag
 */
export function injectFeatureFlag(
    flagName: FeatureFlagName,
    {
        initialValue = getDefaultValue(flagName),
        ...options
    }: InjectFeatureFlagOptions = {},
): Signal<boolean> {
    const facade = options.injector
        ? options.injector.get(FeatureFlagFacade)
        : inject(FeatureFlagFacade);

    return toSignal(facade.isActivated(flagName), {
        initialValue,
        ...options,
    });
}
